import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FiHome, FiLogOut, FiMenu, FiX, FiChevronDown, FiMail, FiBell, FiPlusCircle, FiCheckSquare, FiEye, FiFlag, FiCpu } from 'react-icons/fi';
import { MdOutlineCheckCircle, MdOutlineCancel } from 'react-icons/md';
import { BsClockHistory } from 'react-icons/bs';
import axios from '../../utils/axios';
import { trackLogout, trackAction } from '../../utils/clickTracking';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [pinSearch, setPinSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  // const user = JSON.parse(localStorage.getItem('user')) || { name: 'Unknown', role: 'inspector', rank: 4 };
  // const effectiveRank = String(user?.username || "").trim().toLowerCase() === "inspector9" ? 9 : Number(user.rank);
  // const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user')) || { name: 'Unknown', role: 'inspector', rank: 4 };

  const getEffectiveRank = (u) => {
    const username = String(u?.username || "").trim().toLowerCase();
    const flagType = String(u?.flag_verification_type || "").trim().toLowerCase();

    if (username === "inspector9") return 9;
    if (username === "inspector6") return 6;
    if (username === "inspector1") return 4;
    if (username === "inspector2") return 8;

    if (flagType === "review_cv") return 6;
    if (flagType === "review_supervisor") return 9;
    if (flagType === "add_cv") return 4;
    if (flagType === "add_supervisor") return 8;

    if (flagType === "ai") return 6;
    if (flagType === "supervisor") return 9;

    return Number(u.rank);
  };

  const effectiveRank = getEffectiveRank(user);
  const navigate = useNavigate();

  const unreadNotifications = notifications.filter(prop => !Boolean(prop.is_read));

  const fetchNotifications = async () => {
    if (!user || !user.id) return;
    try {
      setLoadingNotifications(true);
      const res = await axios.get(`/inspector/assigned/${user.id}`);
      const flaggedProperties = res.data || [];
      console.log("Fetched notifications:", flaggedProperties);
      setNotifications(flaggedProperties);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleMarkNotificationRead = async (propertyId) => {
    console.log("handleMarkNotificationRead called with propertyId:", propertyId, "user.id:", user?.id);
    if (!user || !user.id || !propertyId) return;
    try {
      const response = await axios.post(`/inspector/mark-notification-read`, { userId: user.id, propertyId });
      console.log("markNotificationRead response:", response);
      // Update local state to mark as read immediately
      setNotifications(prev => {
        const newNotifications = prev.map(n =>
          n.id === propertyId ? { ...n, is_read: 1 } : n
        );
        console.log("Updated notifications after marking as read:", newNotifications);
        return newNotifications;
      });
    } catch (err) {
      console.error("Failed to mark notification as read:", err.response || err);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Refresh notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user?.id]);

  const handleLogout = async () => {
    try {
      if (user?.id) {
        console.log('🚪 Logging out, tracking first...');
        await trackLogout(user.id, user.role);
        console.log('✅ Logout tracked, now redirecting...');
      }
    } catch (err) {
      console.error('❌ Error tracking logout:', err);
    } finally {
      localStorage.clear();
      window.location.href = '/login';
    }
  };

  const linkClasses = ({ isActive }) =>
    `flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap shrink-0 ${isActive
      ? 'bg-gradient-to-r from-teal-600 to-teal-500 text-white shadow'
      : 'text-gray-700 hover:bg-teal-50 hover:text-teal-600 hover:shadow'
    }`;
  const LABEL_ICONS = {
    'Dashboard': <FiHome className="text-base" />,
    'Add Property': <FiPlusCircle className="text-base" />,
    'Added by Me': <FiCheckSquare className="text-base" />,
    'Pending Review': <BsClockHistory className="text-base" />,
    'Reviewed': <FiEye className="text-base" />,
    'Flagged by Supervisor': <FiFlag className="text-base" />,
    'Flagged by AI': <FiCpu className="text-base" />,
    'Resolved': <MdOutlineCheckCircle className="text-base" />,
    'Not Resolved': <MdOutlineCancel className="text-base" />,
  };

  const getIconForLabel = (label) => LABEL_ICONS[label] || <FiHome className="text-base" />;
  // ✅ Inspector menus with minRank and click id
  const inspectorMenu = [
    { to: '/inspector', label: 'Dashboard', icon: <FiHome className="text-base" />, Rank: 1, clickId: 'dashboard' },
    { to: '/inspector/addProperty', label: 'Add Property', icon: <FiHome className="text-base" />, Rank: 1, clickId: 'add_property' },
    { to: '/inspector/addedbyme', label: 'Added by Me', icon: <FiHome className="text-base" />, Rank: 1, clickId: 'added_by_me' },

    { to: '/inspector', label: 'Dashboard', icon: <FiHome className="text-base" />, Rank: 2, clickId: 'dashboard' },
    { to: '/inspector/assigned', label: 'Pending Review', icon: <BsClockHistory className="text-base" />, Rank: 2, clickId: 'pending_review' },
    { to: '/inspector/reviewed', label: 'Reviewed', icon: <FiHome className="text-base" />, Rank: 2, clickId: 'reviewed' },
    { to: '/inspector/comment', label: 'Flagged by Supervisor', icon: <MdOutlineCheckCircle className="text-base" />, Rank: 2, clickId: 'flagged_by_Supervisor' },
    { to: '/inspector/resolved', label: 'Resolved', icon: <MdOutlineCheckCircle className="text-base" />, Rank: 2, clickId: 'resolved' },

    { to: '/inspector', label: 'Dashboard', icon: <FiHome className="text-base" />, Rank: 3, clickId: 'dashboard' },
    { to: '/inspector/pending-review', label: 'Pending Review', icon: <BsClockHistory className="text-base" />, Rank: 3, clickId: 'pending_review' },
    { to: '/inspector/reviewed', label: 'Reviewed', icon: <FiHome className="text-base" />, Rank: 3, clickId: 'reviewed' },
    { to: '/inspector/comments', label: 'Flagged by Supervisor', icon: <MdOutlineCheckCircle className="text-base" />, Rank: 3, clickId: 'flagged_by_Supervisor' },
    { to: '/inspector/resolved', label: 'Resolved', icon: <MdOutlineCheckCircle className="text-base" />, Rank: 3, clickId: 'resolved' },

    { to: '/inspector', label: 'Dashboard', icon: <FiHome className="text-base" />, Rank: 4, clickId: 'dashboard' },
    { to: '/inspector/addProperty', label: 'Add Property', icon: <FiHome className="text-base" />, Rank: 4, clickId: 'add_property' },
    { to: '/inspector/addedbyme', label: 'Added by Me', icon: <FiHome className="text-base" />, Rank: 4, clickId: 'added_by_me' },
    { to: '/inspector/reviewed-s1d', label: 'Reviewed', icon: <FiHome className="text-base" />, Rank: 4, clickId: 'reviewed' },
    // { to: '/inspector/commented', label: 'Flagged by Supervisor', icon: <MdOutlineCheckCircle className="text-base" />, Rank: 4, clickId: 'flagged_by_Supervisor' },
    { to: '/inspector/flaggedbyalgorithm', label: 'Flagged by Computer Vision', icon: <FiCpu className="text-base" />, Rank: 4, clickId: 'flagged_by_AI' },
    { to: '/inspector/resolved', label: 'Resolved', icon: <MdOutlineCheckCircle className="text-base" />, Rank: 4, clickId: 'resolved' },
    { to: '/inspector/not-resolved', label: 'Not Resolved', icon: <MdOutlineCancel className="text-base" />, Rank: 4, clickId: 'not_resolved' },

    { to: '/inspector', label: 'Dashboard', icon: <FiHome className="text-base" />, Rank: 5, clickId: 'dashboard' },
    { to: '/inspector/reviewed', label: 'Reviewed', icon: <FiHome className="text-base" />, Rank: 5, clickId: 'reviewed' },
    { to: '/inspector/pending-review', label: 'Pending Review', icon: <BsClockHistory className="text-base" />, Rank: 5, clickId: 'pending_review' },

    { to: '/inspector', label: 'Dashboard', icon: <FiHome className="text-base" />, Rank: 6, clickId: 'dashboard' },
    { to: '/inspector/pending-review', label: 'Pending Review', icon: <BsClockHistory className="text-base" />, Rank: 6, clickId: 'pending_review' },
    { to: '/inspector/reviewed', label: 'Reviewed', icon: <FiHome className="text-base" />, Rank: 6, clickId: 'reviewed' },
    { to: '/inspector/flaggedbyalgorithm', label: 'Flagged for Verification by Computer Vision', icon: <FiHome className="text-base" />, Rank: 6, clickId: 'flagged_by_AI' },
    //{ to: '/inspector/comments', label: 'Flagged by Supervisor', icon: <MdOutlineCheckCircle className="text-base" />, Rank: 6, clickId: 'flagged_by_Supervisor' },
    { to: '/inspector/resolved', label: 'Resolved', icon: <MdOutlineCheckCircle className="text-base" />, Rank: 6, clickId: 'resolved' },
    { to: '/inspector/not-resolved', label: 'Not Resolved', icon: <MdOutlineCancel className="text-base" />, Rank: 6, clickId: 'not_resolved' },

    { to: '/inspector', label: 'Dashboard', icon: <FiHome className="text-base" />, Rank: 7, clickId: 'dashboard' },
    { to: '/inspector/addProperty', label: 'Add Property', icon: <FiHome className="text-base" />, Rank: 7, clickId: 'add_property' },
    { to: '/inspector/flaggedbyalgorithm', label: 'Flagged by Computer Vision', icon: <FiHome className="text-base" />, Rank: 7, clickId: 'flagged_by_AI' },
    { to: '/inspector/addedbyme', label: 'Added by Me', icon: <FiHome className="text-base" />, Rank: 7, clickId: 'added_by_me' },

    { to: '/inspector', label: 'Dashboard', icon: <FiHome className="text-base" />, Rank: 8, clickId: 'dashboard' },
    { to: '/inspector/addProperty', label: 'Add Property', icon: <FiHome className="text-base" />, Rank: 8, clickId: 'add_property' },
    { to: '/inspector/addedbyme', label: 'Added by Me', icon: <FiHome className="text-base" />, Rank: 8, clickId: 'added_by_me' },
    { to: '/inspector/reviewed-s1d', label: 'Reviewed', icon: <FiHome className="text-base" />, Rank: 8, clickId: 'reviewed' },
    // { to: '/inspector/flaggedbyalgorithm', label: 'Flagged by AI', icon: <FiHome className="text-base" />, Rank: 8, clickId: 'flagged_by_AI' },
    { to: '/inspector/comments', label: 'Flagged by Supervisor', icon: <MdOutlineCheckCircle className="text-base" />, Rank: 8, clickId: 'flagged_by_Supervisor' },
    { to: '/inspector/resolved', label: 'Resolved', icon: <MdOutlineCheckCircle className="text-base" />, Rank: 8, clickId: 'resolved' },
    { to: '/inspector/not-resolved', label: 'Not Resolved', icon: <MdOutlineCancel className="text-base" />, Rank: 8, clickId: 'not_resolved' },

    { to: '/inspector', label: 'Dashboard', icon: <FiHome className="text-base" />, Rank: 9, clickId: 'dashboard' },
    { to: '/inspector/pending-review', label: 'Pending Review', icon: <BsClockHistory className="text-base" />, Rank: 9, clickId: 'pending_review' },
    { to: '/inspector/reviewed', label: 'Reviewed', icon: <FiHome className="text-base" />, Rank: 9, clickId: 'reviewed' },
    //{ to: '/inspector/flaggedbyalgorithm', label: 'Flagged by AI', icon: <FiHome className="text-base" />, Rank: 9, clickId: 'flagged_by_AI' },
    { to: '/inspector/comments', label: 'Flagged for Verification by Supervisor', icon: <MdOutlineCheckCircle className="text-base" />, Rank: 9, clickId: 'flagged_by_Supervisor' },
    { to: '/inspector/resolved', label: 'Resolved', icon: <MdOutlineCheckCircle className="text-base" />, Rank: 9, clickId: 'resolved' },
    { to: '/inspector/not-resolved', label: 'Not Resolved', icon: <MdOutlineCancel className="text-base" />, Rank: 9, clickId: 'not_resolved' },

  ];

  // ✅ Filter links based on rank
  const menuItems = inspectorMenu.filter(item => effectiveRank == item.Rank);

  const getPendingReviewRouteForRank = (rank) => {
    if (Number(rank) === 2) return "/inspector/assigned";
    return "/inspector/pending-review";
  };

  const getFlaggedBySupervisorRouteForRank = (rank) => {
    const r = Number(rank);
    if (r === 2) return "/inspector/comment";
    if (r === 4) return "/inspector/commented";
    return "/inspector/comments";
  };

  const getReviewedRouteForRank = (rank) => {
    const r = Number(rank);
    if (r === 4 || r === 8) return "/inspector/reviewed-s1d";
    return "/inspector/reviewed";
  };

  const openPinInRelevantPage = async () => {
    const normalizedPin = String(pinSearch || "").trim();
    if (!normalizedPin) return;

    try {
      // Track search action with PIN details
      const user = JSON.parse(localStorage.getItem('user'));
      await trackAction("search_pin", normalizedPin, null, {
        searched_pin: normalizedPin
      });

      setSearching(true);
      const res = await axios.get(`/inspector/property/${encodeURIComponent(normalizedPin)}`);
      const property = res.data;
      const status = String(property?.status || "").trim().toLowerCase();

      if (status === "resolved") {
        navigate(`/inspector/resolved?pin=${encodeURIComponent(normalizedPin)}`);
        return;
      }

      if (status === "flagged") {
        navigate(`${getFlaggedBySupervisorRouteForRank(effectiveRank)}?pin=${encodeURIComponent(normalizedPin)}`);
        return;
      }

      const reviewedByYou =
        String(property?.updated_by ?? "").trim() !== "" &&
        String(property?.updated_by ?? "").trim() === String(user?.id ?? "").trim();

      if (reviewedByYou) {
        navigate(`${getReviewedRouteForRank(effectiveRank)}?pin=${encodeURIComponent(normalizedPin)}`);
        return;
      }

      try {
        const algoRes = await axios.get(`/inspector/algorithm-flagged/${user?.id}`);
        const algoList = algoRes.data || [];
        const isAlgorithmFlagged = algoList.some(
          (p) => String(p?.pin || "").trim().toLowerCase() === normalizedPin.toLowerCase()
        );
        if (isAlgorithmFlagged) {
          navigate(`/inspector/flaggedbyalgorithm?pin=${encodeURIComponent(normalizedPin)}`);
          return;
        }
      } catch { }

      navigate(`${getPendingReviewRouteForRank(effectiveRank)}?pin=${encodeURIComponent(normalizedPin)}`);
    } catch (err) {
      window.alert("No property found against this PIN.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <header className="bg-white shadow-xl border-b border-teal-100 sticky top-0 z-50">
      {/* Main Header */}
      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 lg:px-5 w-full">
        <div className="flex justify-between items-center h-14">
          {/* Left Section - Logo and Brand + Main Nav */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 flex items-center">
              <div className="bg-gradient-to-r from-teal-600 to-teal-500 text-white p-1.5 rounded-lg shadow-lg">
                <FiHome className="h-5 w-5" />
              </div>
              <span className="ml-2 text-lg font-bold bg-gradient-to-r from-teal-600 to-teal-500 bg-clip-text text-transparent">
                Inspector
              </span>
            </div>

            {/* Desktop Main Nav (same line as logo, fills gap) */}
            <nav className="hidden md:flex items-center gap-1 flex-1 justify-center flex-nowrap overflow-x-auto scrollbar-hide [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {menuItems.map((item) => (
                <NavLink
                  key={`${item.to}-${item.Rank}`}
                  to={item.to}
                  end
                  className={linkClasses}
                  data-click-id={item.clickId}
                >
                  {getIconForLabel(item.label)}
                  <span className="text-xs font-semibold">{item.label}</span>
                </NavLink>
              ))}
            </nav>
          </div>

          {/* Right Section - Notifications, User Profile & Mobile Menu */}
          <div className="flex items-center space-x-2.5 flex-shrink-0">
            {/* Notifications Bell */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  if (isProfileOpen) setIsProfileOpen(false);
                }}
                className="p-1.5 bg-teal-50 hover:bg-teal-100 rounded-full transition-all duration-200 border border-teal-200 relative"
              >
                <FiBell className="h-5 w-5 text-teal-700" />
                {unreadNotifications.length > 0 && (
                  <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold leading-none text-white transform translate-x-1/3 -translate-y-1/3 bg-red-500 rounded-full">
                    {unreadNotifications.length}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-teal-100 py-2 z-50 max-h-96 overflow-y-auto">
                  <div className="px-4 py-2 border-b border-gray-100 flex justify-between items-center">
                    <div className="text-sm font-semibold text-gray-800">Notifications</div>
                    {unreadNotifications.length > 0 && (
                      <div className="text-xs text-gray-500">{unreadNotifications.length} new</div>
                    )}
                  </div>
                  {loadingNotifications ? (
                    <div className="px-4 py-6 text-center text-gray-500 text-sm">Loading...</div>
                  ) : notifications.length === 0 ? (
                    <div className="px-4 py-6 text-center text-gray-500 text-sm">No new notifications</div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {notifications.map((property) => (
                        <div
                          key={property.id}
                          className="px-4 py-3 hover:bg-teal-50 transition-colors cursor-pointer"
                          onClick={() => {
                            const flaggedRoute = getFlaggedBySupervisorRouteForRank(effectiveRank);
                            handleMarkNotificationRead(property.id);
                            navigate(`${flaggedRoute}?pin=${encodeURIComponent(property.pin)}`);
                            setShowNotifications(false);
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-semibold text-sm text-gray-800">
                                Property Requiring Attention
                              </div>
                              <div className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full w-fit">
                                <FiFlag className="text-[10px]" />
                                Flagged for Verification by Supervisor
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                PIN: {property.pin}
                              </div>
                              {(property.supervisor_reason || property.reason || property.supervisor_flag_comment) && (
                                <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                                  Reason: {property.supervisor_flag_comment || property.supervisor_reason || property.reason}
                                </div>
                              )}
                              {property.circle && (
                                <div className="text-xs text-teal-700 mt-1">
                                  Circle: {property.circle}
                                </div>
                              )}
                            </div>
                            {!Boolean(property.is_read) && (
                              <div className="ml-2 flex-shrink-0">
                                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* User Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setIsProfileOpen(!isProfileOpen);
                  if (showNotifications) setShowNotifications(false);
                }}
                className="flex items-center space-x-1.5 bg-teal-50 hover:bg-teal-100 rounded-full px-3 py-1.5 transition-all duration-200 border border-teal-200"
              >
                <div className="w-7 h-7 bg-gradient-to-r from-teal-600 to-teal-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-xs font-semibold text-gray-800">{user.name}</div>
                  <div className="text-[10px] text-gray-500">{user.username}</div>
                </div>
                <FiChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Profile Dropdown Menu */}
              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-2xl border border-teal-100 py-2 z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <div className="text-sm font-semibold text-gray-800">{user.name}</div>
                    <div className="text-xs text-gray-500">Circle :{user.circle}</div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 transition-colors text-sm font-medium"
                  >
                    <FiLogOut className="text-lg" />
                    Logout
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg bg-teal-50 hover:bg-teal-100 text-teal-70 transition-colors"
            >
              {isMenuOpen ? <FiX className="h-6 w-6" /> : <FiMenu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 lg:px-5 w-full pb-2">
        <div className="flex items-center gap-2 bg-white p-1.5 rounded-lg border border-gray-200 shadow-sm">
          <input
            type="text"
            value={pinSearch}
            onChange={(e) => setPinSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && openPinInRelevantPage()}
            placeholder="Search PIN..."
            className="flex-1 border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-1 focus:ring-teal-500 outline-none"
          />
          <button
            onClick={openPinInRelevantPage}
            disabled={searching}
            className="px-3 py-1 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-60"
          >
            {searching ? "..." : "Search"}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-teal-100 shadow-inner">
          <div className="px-4 py-3 space-y-1">
            {menuItems.map((item) => (
              <NavLink
                key={`${item.to}-${item.Rank}`}
                to={item.to}
                end
                className={linkClasses}
                onClick={() => setIsMenuOpen(false)}
                data-click-id={item.clickId}
              >
                {getIconForLabel(item.label)}
                <span className="font-semibold">{item.label}</span>
              </NavLink>
            ))}

            {/* Mobile Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium mt-4 border border-red-100"
            >
              <FiLogOut className="text-lg" />
              Logout
            </button>
          </div>
        </div>
      )}
      {showEmail && (
        <div className="fixed bottom-24 right-6 z-[60] p-3 bg-emerald-50 rounded-lg border border-emerald-200 shadow-md">
          <span className="text-emerald-700 font-medium">0341-0925767</span>
        </div>
      )}
      <button
        onClick={() => setShowEmail((prev) => !prev)}
        className="fixed bottom-6 right-6 z-[60] flex items-center gap-2 px-4 py-3 rounded-full shadow-lg border border-teal-200 bg-teal-600 text-white hover:bg-teal-700 transition-colors"
        title="Contact Admin"
        data-click-id="contact_admin"
      >
        <FiMail className="text-base" />
        <span className="text-sm font-semibold">Contact Admin</span>
      </button>
    </header>
  );
};

export default Header;
